<template>
  <fts-design-modal
    :append-to-body="appendToBody"
    :backdrop="!submitting"
    :escape-event="false"
    :disabled-close="submitting"
    append-content-class="widget-design-preview-modal__body"
    class="fts-widget-design-preview-modal"
    @closed="reset"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <template v-if="step !== 1 && widgetData.widgetType === 'chart'">
          <font-awesome-icon :icon="['fas', 'chart-simple']" class="design-modal-header-icon" />
          <div class="design-modal-header-name design-modal-header-name--flex-1">Chart</div>
        </template>

        <template v-else>
          <font-awesome-icon :icon="['fas', 'chart-column']" class="design-modal-header-icon" />
          <div class="design-modal-header-name design-modal-header-name--flex-1">Analyse</div>
        </template>
      </div>
    </template>
    <!-- body -->
    <template #body>
      <!-- preview -->
      <div
        class="widget-design-preview-chart"
        v-if="step == 3 && widgetData.widgetType === 'chart'"
      >
        <chart
          :modules="modules"
          :states="states"
          :users="users"
          :item="widgetData"
          preview
          :card-mode="false"
          class="fts-form-group fts-form-group--comfort"
          ref="chart-preview"
        />

        <fts-form-element
          :form-data="widgetData"
          :item="{
            type: 0,
            fieldName: 'name',
            displayName: widgetNameRequired ? 'Chart Name' : 'Chart Name (optional)',
            required: widgetNameRequired
          }"
          :validator="$v.widgetData"
          comfort-design
          v-if="isAdminUser"
        />
      </div>
      <!-- settings -->
      <div
        v-else-if="step == 2 && widgetData.widgetType === 'chart'"
      >
        <fts-widget-chart-design
          :modules="modules"
          :chart-data.sync="widgetData"
          source="listView"
          ref="chart-design"
        />
      </div>
      <!-- analyse -->

      <div class="widget-design-preview-selection" v-else>
        <div
          class="widget-design-preview-option"
          @click="openDesign('chart')"
        >
          <font-awesome-icon :icon="['fas', 'chart-simple']" class="widget-design-preview-option-icon" /> 
          <div class="widget-design-preview-option-label">Chart</div>
        </div>
      </div>
    </template>
    <!-- footer:right -->
    <template #footerLeft>
      <fts-btn
        text
        :disabled="submitting"
        @click="back"
        v-if="step > 1"
      >
        Back
      </fts-btn>
    </template>

    <template #footerRight>
      <fts-btn
        text
        @click="close"
        v-if="step == 1"
      >
        Cancel
      </fts-btn>

      <fts-btn
        color="primary"
        outline
        @click="next"
        v-if="step == 2"
      >
        Next
      </fts-btn>

      <fts-btn
        color="primary"
        outline
        :loading="submitting"
        @click="done"
        v-if="step == 3 && isAdminUser"
      >
        Add to Dashboard
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import { requiredIf } from 'vuelidate/lib/validators';

import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import queryLogic from '../../commons/queryLogic';
import commons from '../../commons/commons';

import Chart from '../widgets/Chart';
import FTSBtn from '../../elements/FTSBtn';
import FTSDesignModal from '../../design/FTSDesignModal';
import FTSFormElement from '../../elements/FTSFormElement';
import FTSReleaseBadge from '../../elements/FTSReleaseBadge';
import FTSWidgetChartDesign from './FTSWidgetChartDesign';

export default {
  components: {
    'chart': Chart,
    'fts-btn': FTSBtn,
    'fts-design-modal': FTSDesignModal,
    'fts-form-element': FTSFormElement,
    'fts-release-badge': FTSReleaseBadge,
    'fts-widget-chart-design': FTSWidgetChartDesign
  },
  props: {
    appendToBody: Boolean,
    modules: Array,
    states: Array,
    users: Array
  },
  data: () => ({
    step: 1,
    widgetData: null,
    submitting: false
  }),
  validations() {
    let widgetData = {
      name: {
        required: requiredIf(() => this.widgetNameRequired)
      }
    };
    return { widgetData };
  },
  methods: {
    async show(widgetData) {
      this.widgetData = {...widgetData};
      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show();
      });
    },
    close() {
      this.$refs.ftsDesignModal.close();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async done() {
      this.$v.widgetData.$touch();
      if (!this.$v.widgetData.$invalid) {
        this.submitting = true;
        try {
          await clientStore.dispatch('previewWidget', {
            ...this.widgetData,
            save: true
          });
          this.$toasted.success("Widget successfully added to the dashboard");
          this.resolve && this.resolve(this.widgetData);
          this.submitting = false;
          this.close();
        } catch(err) {
          console.error(err);
          this.$toasted.global.error({ response: err.response });
          this.submitting = false;
        }
      } else {
        this.$toasted.global.invalidFormValidate();
      }
    },
    next() {
      if (this.step == 2) {
        const validate = this.$refs['chart-design'].validateForm();
        if (validate.$invalid) {
          return;
        }
      }

      if (this.step < 3) {
        this.step += 1;
      }
    },
    back() {
      if (this.step > 1) {
        this.step -= 1;
      }
    },
    openDesign(widgetType) {
      if (this.widgetData.widgetType && this.widgetData.widgetType != widgetType) {
        this.widgetData = {};
      }

      if (!this.widgetData.id) {
        this.$set(this.widgetData, "id", commons.nanoID());
      }

      if (widgetType === "chart") {
        this.$set(this.widgetData, "widgetType", "chart");
      } else {
        this.$toasted.error("Something went wrong");
        return;
      }

      this.step = 2;
    }
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    widgetNameRequired() {
      if (this.widgetData && this.widgetData.filters) {
        return queryLogic.getFiltersValidity(this.widgetData.filters);
      }
      return false;
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-widget-design-preview-modal {
  .widget-design-preview-modal__body {
    padding-top: 0.25rem;
  }
  .widget-design-preview-selection {
    display: grid;
    gap: 1.5rem;
  }
  .widget-design-preview-option {
    padding: 1.5rem 1rem;
    border-radius: $comfort-border-radius;
    box-shadow: var(--fts-elevation-2);
    text-align: center;
    color: var(--fts-text);
    transition: box-shadow 0.3s ease-in-out;
    .widget-design-preview-option-icon {
      font-size: 1.375rem;
    }
    .widget-design-preview-option-label {
      margin-top: 0.75rem;
      font-size: 1rem;
      font-weight: 500;
    }
    &:hover {
      box-shadow: var(--fts-elevation-3);
      cursor: pointer;
    }
  }
  @media (min-width: 768px) {
    .widget-design-preview-selection {
      grid-template-columns: repeat(2,minmax(0,1fr));
    }
  }
  @media (min-width: 1024px) {
    .widget-design-preview-selection {
      grid-template-columns: repeat(3,minmax(0,1fr));
    }
  }
}
</style>